import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus } from "@phosphor-icons/react";
import { fmtYmd } from "@/lib/dateFormat";
import DatePickerYmd from "@/components/DatePickerYmd";

export default function Maintenance() {
    const [list, setList] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [kinds, setKinds] = useState([]);  // from /api/taxonomies/maintenance_kind
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        vehicle_id: "", performed_at: "", kind: "oil_change", description: "", cost_yen: 0, vendor: "",
    });

    const load = async () => {
        const [m, v, k] = await Promise.all([
            api.get("/maintenance"),
            api.get("/vehicles"),
            api.get("/taxonomies/maintenance_kind"),
        ]);
        setList(m.data);
        setVehicles(v.data);
        setKinds(k.data);
    };
    useEffect(() => { load(); }, []);

    const enabledKinds = kinds.filter((k) => k.enabled);
    const kindLookup = (val) => kinds.find((k) => k.value === val) || { label: val, color: "#888" };

    const submit = async (e) => {
        e.preventDefault();
        try {
            await api.post("/maintenance", { ...form, cost_yen: parseInt(form.cost_yen || 0) });
            toast.success("整備記録を登録しました");
            setOpen(false);
            setForm({ vehicle_id: "", performed_at: "", kind: "oil_change", description: "", cost_yen: 0, vendor: "" });
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-5" data-testid="maintenance-page">
            <div className="flex justify-between items-end flex-wrap gap-3">
                <div>
                    <p className="prc-label-overline">整備管理</p>
                    <h1 className="text-[1.685rem] font-bold tracking-tight">整備記録一覧</h1>
                </div>
                <button onClick={() => setOpen(true)} className="prc-btn-primary inline-flex items-center gap-2" data-testid="maint-new-btn">
                    <Plus size={16} />整備を記録
                </button>
            </div>

            <div className="prc-card overflow-x-auto">
                {list.length === 0 ? (
                    <p className="p-8 text-center text-sm text-[var(--prc-muted)]" data-testid="maint-empty">整備記録がありません</p>
                ) : (
                    <table className="prc-table" data-testid="maint-table">
                        <thead>
                            <tr><th>実施日</th><th>車両</th><th>種別</th><th>内容</th><th>ベンダー</th><th className="text-right">費用</th></tr>
                        </thead>
                        <tbody>
                            {list.map((m) => (
                                <tr key={m.id} data-testid={`maint-row-${m.id}`}>
                                    <td className="num">{fmtYmd(m.performed_at, m.performed_at)}</td>
                                    <td className="font-mono">{m.vehicle_plate || "-"}</td>
                                    <td>
                                        {(() => {
                                            const k = kindLookup(m.kind);
                                            return (
                                                <span
                                                    className="inline-block px-2 py-0.5 border rounded-sm text-[10px] font-mono uppercase tracking-wider"
                                                    style={{
                                                        color: k.color,
                                                        borderColor: (k.color || "#888") + "66",
                                                        backgroundColor: (k.color || "#888") + "1a",
                                                    }}
                                                >
                                                    {k.label}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td className="max-w-sm truncate">{m.description}</td>
                                    <td>{m.vendor || "-"}</td>
                                    <td className="num">¥{(m.cost_yen || 0).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {open && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="maint-dialog">
                    <form onSubmit={submit} className="prc-card p-6 max-w-lg w-full">
                        <p className="prc-label-overline mb-1">整備記録</p>
                        <h2 className="text-xl font-bold mb-4">新規登録</h2>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                                <label className="prc-label-overline block mb-1">車両 *</label>
                                <select required className="prc-input" value={form.vehicle_id} onChange={(e) => setForm({ ...form, vehicle_id: e.target.value })} data-testid="maint-vehicle">
                                    <option value="">選択</option>
                                    {vehicles.map((v) => <option key={v.id} value={v.id}>{v.plate_number} — {v.make} {v.model}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="prc-label-overline block mb-1">実施日 *</label>
                                <DatePickerYmd value={form.performed_at} onChange={(val) => setForm({ ...form, performed_at: val })} required testid="maint-date" />
                            </div>
                            <div>
                                <label className="prc-label-overline block mb-1">種別</label>
                                <select className="prc-input" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="maint-kind">
                                    {enabledKinds.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                                </select>
                            </div>
                            <div className="col-span-2">
                                <label className="prc-label-overline block mb-1">内容 *</label>
                                <textarea required className="prc-input min-h-20" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="maint-desc" />
                            </div>
                            <div>
                                <label className="prc-label-overline block mb-1">費用 (円)</label>
                                <input type="number" className="prc-input" value={form.cost_yen} onChange={(e) => setForm({ ...form, cost_yen: e.target.value })} data-testid="maint-cost" />
                            </div>
                            <div>
                                <label className="prc-label-overline block mb-1">ベンダー</label>
                                <input className="prc-input" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="maint-vendor" />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <button type="button" onClick={() => setOpen(false)} className="prc-btn-ghost" data-testid="maint-cancel">キャンセル</button>
                            <button type="submit" className="prc-btn-primary" data-testid="maint-submit">登録</button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
